import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
    ChevronUp,
    Info,
    Search,
    Loader2,
    Pencil,
    Trash2,
    ChevronDown,
    XCircle,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import Input from "./Input";
import Button from "./Button";
import Dropdown from "./Dropdown";

export const DataTable2 = ({
    columns,
    data,
    loading = false,
    error = null,
    emptyMessage = "No data available.",
    actionsHeader,
    enableSearch = true,
    enablePagination = false,
    rowsPerPage = 10,
    onEdit,
    onDelete,
    onAction,
    rowActions = [],
}) => {
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [openDropdownRow, setOpenDropdownRow] = useState(null);

    const hasRowActions = useMemo(
        () => !!(onEdit || onDelete || rowActions.length > 0),
        [onEdit, onDelete, rowActions.length]
    );

    // FILTERING logic remains identical to ensure no data loss
    const filteredData = useMemo(() => {
        let cur = data;
        if (enableSearch && search.trim()) {
            const lower = search.toLowerCase();
            cur = cur.filter((row) =>
                columns.some((col) => {
                    const val = typeof col.searchable === "function" ? col.searchable(row) : row[col.key];
                    return val != null && String(val).toLowerCase().includes(lower);
                })
            );
        }
        return cur;
    }, [search, data, columns, enableSearch]);

    // PAGINATION logic
    const paginatedData = useMemo(() => {
        if (!enablePagination) return filteredData;
        const start = (currentPage - 1) * rowsPerPage;
        return filteredData.slice(start, start + rowsPerPage);
    }, [filteredData, currentPage, rowsPerPage, enablePagination]);

    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    const goPrev = () => setCurrentPage((p) => Math.max(p - 1, 1));
    const goNext = () => setCurrentPage((p) => Math.min(p + 1, totalPages));
    const toggleRowDropdown = (idx) => setOpenDropdownRow((cur) => (cur === idx ? null : idx));

    return (
        <div className="flex flex-col h-full rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">

            {/* 1. OPTIMIZED HEADER: Pixel-Point Responsiveness */}
            <div className="sticky top-0 z-30 flex flex-col gap-4 border-b border-gray-100 bg-white p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
                {enableSearch && (
                    <div className="relative w-full lg:max-w-xs">
                        <Input
                            type="text"
                            placeholder="Search records..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                            className="w-full pl-10 pr-4 h-10 text-sm"
                            icon={<Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />}
                        />
                    </div>
                )}
                {actionsHeader && (
                    <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1 lg:pb-0">
                        {actionsHeader}
                    </div>
                )}
            </div>

            {/* 2. OPTIMIZED BODY: Horizontal Scroll Safety */}
            <div className="flex-1 overflow-auto min-w-0 bg-gray-50/30 custom-scrollbar">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <Loader2 className="animate-spin text-gmail-blue" size={40} />
                        <p className="mt-4 text-gray-500">Retrieving data...</p>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center px-4">
                        <XCircle className="text-gmail-red mb-3" size={48} />
                        <p className="text-lg font-bold text-gray-800">Connection Error</p>
                        <p className="text-sm text-gray-500 max-w-xs">{error}</p>
                    </div>
                ) : paginatedData.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                        <Info className="text-gray-300 mb-2" size={48} />
                        <p className="text-gray-500">{emptyMessage}</p>
                    </div>
                ) : (
                    <>
                        {/* DESKTOP VIEW: Enabled Horizontal Scroll */}
                        <div className="hidden lg:block min-w-0">
                            <table className="w-full table-auto text-sm text-gray-800">
                                <thead className="sticky top-0 z-10 bg-gray-50/90 backdrop-blur-sm border-b border-gray-200">
                                    <tr>
                                        {columns.map((col) => (
                                            <th key={col.key} className="px-6 py-4 text-left font-bold text-gray-600 uppercase tracking-wider text-[11px]">
                                                {col.label}
                                            </th>
                                        ))}
                                        {hasRowActions && <th className="px-6 py-4 text-right font-bold text-gray-600 text-[11px] uppercase tracking-wider">Actions</th>}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 bg-white">
                                    {paginatedData.map((row, i) => (
                                        <motion.tr
                                            key={row.id || i}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="hover:bg-blue-50/30 transition-colors group"
                                        >
                                            {columns.map((col) => (
                                                <td key={col.key} className="px-6 py-4 whitespace-nowrap max-w-[250px] truncate group-hover:overflow-visible group-hover:whitespace-normal">
                                                    {col.render ? col.render(row[col.key], row) : row[col.key] ?? <span className="text-gray-300">-</span>}
                                                </td>
                                            ))}
                                            {hasRowActions && (
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center justify-end gap-1 opacity-80 group-hover:opacity-100">
                                                        {onEdit && <Button variant="ghost" size="sm" onClick={() => onEdit(row)} className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg"><Pencil size={16} /></Button>}
                                                        {onDelete && <Button variant="ghost" size="sm" onClick={() => onDelete(row)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg"><Trash2 size={16} /></Button>}
                                                        {rowActions.length > 0 && (
                                                            <Dropdown align="right" trigger={<Button variant="ghost" size="sm" className="p-2 hover:bg-gray-100 rounded-lg"><ChevronDown size={16} /></Button>}>
                                                                <div className="py-1 min-w-[160px]">
                                                                    {rowActions.map(act => (act.shouldRender?.(row) ?? true) && (
                                                                        <button key={act.key} onClick={() => { onAction?.(act.key, row); setOpenDropdownRow(null); }} className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                                                                            {act.icon} <span>{act.label}</span>
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </Dropdown>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                        </motion.tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* MOBILE CARD VIEW: Grid Strategy */}
                        <div className="lg:hidden p-4 space-y-4">
                            {paginatedData.map((row, i) => (
                                <motion.div key={row.id || i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            {columns.map((col, cIdx) => (
                                                <div key={col.key} className={`${cIdx === 0 ? 'col-span-2 border-b border-gray-50 pb-2' : ''}`}>
                                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">{col.label}</p>
                                                    <div className={`${cIdx === 0 ? 'text-base font-bold' : 'text-sm'} text-gray-800 break-words`}>
                                                        {col.render ? col.render(row[col.key], row) : row[col.key] ?? "-"}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {hasRowActions && (
                                            <div className="flex justify-end gap-1 pt-3 border-t border-gray-100">
                                                {onEdit && <Button variant="ghost" size="sm" onClick={() => onEdit(row)} className="text-blue-600 p-2"><Pencil size={18} /></Button>}
                                                {onDelete && <Button variant="ghost" size="sm" onClick={() => onDelete(row)} className="text-red-500 p-2"><Trash2 size={18} /></Button>}
                                                {rowActions.length > 0 && (
                                                    <Dropdown align="right" trigger={<Button variant="ghost" size="sm" className="p-2"><ChevronDown size={18} /></Button>}>
                                                        <div className="py-1 min-w-[180px]">
                                                            {rowActions.map(act => (act.shouldRender?.(row) ?? true) && (
                                                                <button key={act.key} onClick={() => { onAction?.(act.key, row); }} className="flex w-full items-center gap-3 px-4 py-3 text-sm hover:bg-gray-50 border-b border-gray-50 last:border-0">
                                                                    {act.icon} {act.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </Dropdown>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* 3. OPTIMIZED PAGINATION: Compact Scaling */}
            {enablePagination && totalPages > 1 && (
                <div className="sticky bottom-0 z-30 flex items-center justify-between border-t border-gray-100 bg-white px-4 py-3 sm:px-6">
                    <Button variant="secondary" onClick={goPrev} disabled={currentPage === 1 || loading} className="gap-1 px-3">
                        <ChevronLeft size={16} /> <span className="hidden sm:inline">Prev</span>
                    </Button>
                    <div className="flex flex-col items-center">
                        <span className="text-xs font-medium text-gray-500">Page</span>
                        <span className="text-sm font-bold text-gray-800">{currentPage} / {totalPages}</span>
                    </div>
                    <Button variant="secondary" onClick={goNext} disabled={currentPage === totalPages || loading} className="gap-1 px-3">
                        <span className="hidden sm:inline">Next</span> <ChevronRight size={16} />
                    </Button>
                </div>
            )}
        </div>
    );
};